import { Helmet } from 'react-helmet-async';
import React, { useState, useEffect } from 'react';
import { useParams, NavLink } from 'react-router-dom';
import Header from './../Common/Header';
import Footer from './../Common/Footer';
import Banner from './../Elements/Banner';
import { productsData } from '../../data/siteData';
import { getProductBySlug, getProducts, resolveImageUrl as apiResolveImageUrl } from '../../services/api';
import '../../styles/pages/ProductDetail.css';

var bnrimg = require('./../../images/banner/6.jpg');


const DynamicProductDetail = () => {
    const { id } = useParams();

    const [product, setProduct] = useState(() => {
        return productsData.find(item => String(item.id) === String(id) || item.slug === id) || null;
    });
    const [allProducts, setAllProducts] = useState(productsData);

    useEffect(() => {
        let isMounted = true;

        // Reset local match for new slug/id
        const localMatch = productsData.find(item => String(item.id) === String(id) || item.slug === id);
        if (localMatch) {
            setProduct(localMatch);
        }

        async function loadProductData() {
            try {
                // 1. Fetch full product list from backend API first
                const apiList = await getProducts({}, productsData);
                if (isMounted && Array.isArray(apiList) && apiList.length > 0) {
                    setAllProducts(apiList);

                    // Find matching product in API list
                    const targetInList = apiList.find(item =>
                        String(item.id) === String(id) ||
                        item.slug === id ||
                        (item.slug && item.slug.toLowerCase() === String(id).toLowerCase()) ||
                        (item.id && String(item.id).toLowerCase() === String(id).toLowerCase())
                    );

                    if (targetInList) {
                        setProduct(targetInList);
                        return;
                    }
                }

                // 2. Fetch specific product by slug if not in list
                const apiProd = await getProductBySlug(id, null);
                if (isMounted && apiProd) {
                    setProduct(apiProd);
                } else if (isMounted && !localMatch) {
                    const fallbackMatch = productsData.find(item => String(item.id) === String(id) || item.slug === id) || productsData[0];
                    setProduct(fallbackMatch);
                }
            } catch (e) {
                console.warn('[DynamicProductDetail] API load fallback:', e);
                if (isMounted && !localMatch) {
                    const fallbackMatch = productsData.find(item => String(item.id) === String(id) || item.slug === id) || productsData[0];
                    setProduct(fallbackMatch);
                }
            }
        }

        loadProductData();
        return () => { isMounted = false; };
    }, [id]);

    const activeProduct = product || productsData.find(item => String(item.id) === String(id) || item.slug === id) || productsData[0];
    const otherProducts = allProducts.filter(item => String(item.id) !== String(activeProduct.id) && item.slug !== activeProduct.slug);

    const rawFeatures = activeProduct.features;
    let features = [];
    if (Array.isArray(rawFeatures)) {
        features = rawFeatures;
    } else if (typeof rawFeatures === 'string') {
        try {
            const parsed = JSON.parse(rawFeatures);
            features = Array.isArray(parsed) ? parsed : [rawFeatures];
        } catch (e) {
            features = rawFeatures.split(',').map(f => f.trim()).filter(Boolean);
        }
    }

    const rawSpecs = activeProduct.specifications;
    let specifications = [];
    if (Array.isArray(rawSpecs)) {
        specifications = rawSpecs;
    } else if (typeof rawSpecs === 'string') {
        try {
            const parsed = JSON.parse(rawSpecs);
            specifications = Array.isArray(parsed) ? parsed : [];
        } catch (e) {
            specifications = [];
        }
    }

    const resolveImageUrl = (img, fallback = null) => {
        if (!img) return fallback;
        if (typeof img === 'string') {
            if (img.startsWith('http://') || img.startsWith('https://') || img.startsWith('data:')) return img;
            return apiResolveImageUrl(img);
        }
        return img || fallback;
    };

    const defaultFallbackImg = require('./../../images/products/acp_cladding.jpg');
    const mainImg = resolveImageUrl(activeProduct.mainImage || activeProduct.image, defaultFallbackImg);
    const rawBannerVal = activeProduct.bannerImage || activeProduct.bgImage;
    const bannerImg = rawBannerVal ? resolveImageUrl(rawBannerVal, null) : mainImg;
    const fullDescription = activeProduct.fullDesc || activeProduct.description || `High-performance architectural ${activeProduct.category || 'cladding'} solutions by bs clads in Coimbatore.`;
    const shortDescription = activeProduct.shortDesc || activeProduct.subtitle || `Architectural ${activeProduct.category || 'elevation'} solutions.`;

    return (
        <>
            <Helmet>
                <title>bs clads</title>
                <meta name="description" content={shortDescription} />
                <meta property="og:title" content={`${activeProduct.title} - bs clads`} />
                <meta property="og:description" content={shortDescription} />
            </Helmet>
            <Header />
            <div className="page-content">
                <Banner
                    pageKey="product-detail"
                    bannerKey={activeProduct.slug || activeProduct.id}
                    title={activeProduct.title}
                    pagename={`Products / ${activeProduct.title}`}
                    description={shortDescription}
                    bgimage={bannerImg}
                />

                <div className="section-full p-tb80 bg-white">
                    <div className="container">
                        <div className="row">
                            {/* MAIN PRODUCT DETAILS */}
                            <div className="col-lg-8 col-md-12 m-b30">
                                <div className="product-detail-box">
                                    {/* HERO PRODUCT IMAGE */}
                                    <div className="sx-post-media m-b30 wow zoomIn" data-wow-duration="1.2s" style={{ borderRadius: '10px', overflow: 'hidden', height: '420px', boxShadow: '0 8px 25px rgba(0,0,0,0.1)' }}>
                                        <img src={mainImg} alt={activeProduct.title} onError={(e) => { e.target.onerror = null; e.target.src = require('./../../images/products/acp_cladding.jpg'); }} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                    </div>

                                    <h2 className="m-b15 wow fadeInUp" data-wow-duration="1s" style={{ fontWeight: '800', color: '#222' }}>{activeProduct.title}</h2>
                                    <h5 className="m-b20 text-muted wow fadeInUp" data-wow-duration="1s" data-wow-delay="0.1s" style={{ color: '#f03a02', fontWeight: '600' }}>
                                        {activeProduct.category || activeProduct.subtitle}
                                    </h5>

                                    <p className="m-b30 wow fadeInUp" data-wow-duration="1s" data-wow-delay="0.2s" style={{ fontSize: '16px', lineHeight: '1.8', color: '#444' }}>
                                        {fullDescription}
                                    </p>

                                    {/* KEY PERFORMANCE FEATURES */}
                                    {features.length > 0 && (
                                        <div className="p-a30 bg-gray m-b30 wow fadeInUp" data-wow-duration="1s" data-wow-delay="0.3s" style={{ borderRadius: '8px' }}>
                                            <h4 className="m-t0 m-b20" style={{ fontWeight: '700' }}>Key Performance Features</h4>
                                            <div className="row">
                                                {features.map((feature, fIdx) => (
                                                    <div className="col-md-6 col-sm-12 m-b15" key={fIdx}>
                                                        <div className="d-flex align-items-start">
                                                            <i className="fa fa-check-circle text-primary m-r10 m-t5" style={{ color: '#f03a02', fontSize: '18px' }} />
                                                            <span style={{ fontSize: '15px', color: '#333' }}>{feature}</span>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* TECHNICAL SPECIFICATIONS TABLE */}
                                    {specifications.length > 0 && (
                                        <div className="m-b40 wow fadeInUp" data-wow-duration="1s" data-wow-delay="0.3s">
                                            <h3 className="m-b20" style={{ fontWeight: '800', fontSize: '24px', color: '#111111' }}>
                                                Technical Specifications
                                            </h3>
                                            <div style={{
                                                borderRadius: '10px',
                                                overflow: 'hidden',
                                                border: '1px solid #e9ecef',
                                                boxShadow: '0 4px 15px rgba(0,0,0,0.03)',
                                                backgroundColor: '#ffffff'
                                            }}>
                                                <table className="table m-b0" style={{ margin: 0, width: '100%', borderCollapse: 'collapse' }}>
                                                    <tbody>
                                                        {specifications.map((spec, sIdx) => (
                                                            <tr key={sIdx} style={{ borderBottom: sIdx === specifications.length - 1 ? 'none' : '1px solid #e9ecef' }}>
                                                                <th style={{
                                                                    width: '38%',
                                                                    padding: '16px 20px',
                                                                    backgroundColor: '#ffffff',
                                                                    color: '#111111',
                                                                    fontWeight: '800',
                                                                    fontSize: '15px',
                                                                    borderRight: '1px solid #e9ecef',
                                                                    borderBottom: 'none',
                                                                    verticalAlign: 'middle'
                                                                }}>
                                                                    {spec.key}
                                                                </th>
                                                                <td style={{
                                                                    padding: '16px 20px',
                                                                    color: '#555555',
                                                                    fontSize: '15px',
                                                                    borderBottom: 'none',
                                                                    verticalAlign: 'middle',
                                                                    lineHeight: '1.6'
                                                                }}>
                                                                    {spec.value}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    )}

                                    {/* INQUIRY ACTION */}
                                    <div className="p-a30 bg-dark text-white d-flex justify-content-between align-items-center flex-wrap m-b30 wow fadeInUp" data-wow-duration="1s" data-wow-delay="0.4s" style={{ borderRadius: '8px', backgroundColor: '#1a1a1a' }}>
                                        <div>
                                            <h4 className="text-white m-t0 m-b5">Interested in {activeProduct.title}?</h4>
                                            <p className="m-b0" style={{ color: '#aaa' }}>Request custom pricing, material samples, and technical drawings.</p>
                                        </div>
                                        <NavLink to="/contact-us" className="site-button btn-half m-t10" style={{ backgroundColor: '#f03a02', border: 'none' }}>
                                            <span>Request Quote</span>
                                        </NavLink>
                                    </div>

                                    {/* BACK TO PRODUCTS BUTTON */}
                                    <div className="wow fadeInUp" data-wow-duration="0.8s" data-wow-delay="0.5s">
                                        <NavLink to="/products" className="site-button btn-half">
                                            <span>&larr; Back to All Products</span>
                                        </NavLink>
                                    </div>
                                </div>
                            </div>

                            {/* OTHER PRODUCTS SIDEBAR */}
                            <div className="col-lg-4 col-md-12 wow fadeInRight" data-wow-duration="1s" data-wow-delay="0.2s">
                                <div className="side-bar p-a30 bg-gray" style={{ borderRadius: '8px' }}>
                                    <h5 className="widget-title m-b20" style={{ borderBottom: '2px solid #f03a02', paddingBottom: '8px', fontWeight: '700' }}>
                                        All Products &amp; Services
                                    </h5>
                                    <ul className="list-unstyled" style={{ lineHeight: '2.5' }}>
                                         {otherProducts.map((oProd, oIdx) => {
                                             const targetSidebarSlug = (oProd.slug && oProd.slug.trim()) ? oProd.slug.trim() : (oProd.id || 'acp-front-elevation');
                                             return (
                                                 <li key={oIdx} style={{ borderBottom: '1px solid #e0e0e0', paddingBottom: '8px', marginBottom: '8px' }}>
                                                     <NavLink to={`/product/${encodeURIComponent(targetSidebarSlug)}`} style={{ color: '#333', fontWeight: '600', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                         <span>{oProd.title}</span>
                                                         <i className="fa fa-angle-right" style={{ color: '#f03a02' }} />
                                                     </NavLink>
                                                 </li>
                                             );
                                         })}
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <Footer />
        </>
    );
};

export default DynamicProductDetail;
